import React from 'react';
import Footer from './footer';
import Header from './header';
import ReactGA from 'react-ga4';
import { Reveal, Scramble, d, useParallax } from './motion';

ReactGA.send(window.location.pathname);

const capabilities = [
    {
        label: 'Mobile Apps',
        desc: 'Native iOS and Android apps built for real people.',
    },
    {
        label: 'Web',
        desc: 'Fast, focused web products that do exactly what they need to.',
    },
    {
        label: 'AI',
        desc: 'AI built into our apps where it genuinely makes a difference.',
    },
    {
        label: 'Design',
        desc: 'Interfaces that get out of the way and let the product speak.',
    },
];

const products = [
    {
        name: 'Corbeau',
        tag: 'Intelligence Platform',
        desc: 'Predictive Threat Intelligence',
        href: 'https://corbeau.tech',
        cta: 'corbeau.tech',
    },
    {
        name: 'Peticle',
        tag: 'iOS App',
        desc: 'AI-powered pet health analysis and tracker.',
        href: 'https://peticle.app',
        cta: 'peticle.app',
    },
    {
        name: 'Bundu',
        tag: 'Mobile App',
        desc: 'Connects you to outdoor experiences near you.',
        href: 'https://bundu.club/',
        cta: 'bundu.club',
    },
];

const pad = (n: number) => String(n).padStart(2, '0');

function SectionLabel({ index, title }: { index: number; title: string }) {
    return (
        <div className="mono text-[#7a7873] mb-10 lg:mb-0">
            <Scramble text={`(${pad(index)}) ${title}`} />
        </div>
    );
}

function Hero() {
    const markRef = useParallax<SVGSVGElement>(0.18);

    return (
        <section className="relative min-h-[100svh] flex flex-col overflow-hidden">
            <svg ref={markRef} className="hero-mark" viewBox="0 0 1000 420" aria-hidden="true">
                <text x="500" y="370" textAnchor="middle" fontSize="420" fontWeight="500" letterSpacing="-20"
                      style={{ fontFamily: 'var(--font-sans)' }}>
                    326
                </text>
            </svg>

            <span className="crosshair hidden md:block" style={{ top: '31%', right: '12%' }} />
            <span className="crosshair hidden md:block" style={{ bottom: '24%', left: '48%' }} />

            <Reveal className="shell relative pt-20 sm:pt-24">
                <div className="rule" style={d(200)} />
                <div className="flex justify-between py-4 mono text-[#7a7873]">
                    <span className="fade" style={d(500)}>Software studio</span>
                    <span className="fade hidden sm:inline" style={d(650)}>Apps · Web · AI · Design</span>
                </div>
            </Reveal>

            <Reveal className="shell relative flex-1 flex flex-col justify-end pb-12 sm:pb-16">
                <h1 className="display text-[clamp(3.4rem,14.5vw,11.5rem)] lg:text-[clamp(3rem,11vw,11.5rem)]">
                    <span className="mask"><span style={d(300)}>We build</span></span>
                    <span className="mask"><span style={d(420)}>things that</span></span>
                    <span className="mask text-[#4a4945]"><span style={d(540)}>work.</span></span>
                </h1>

                <div className="mt-10 sm:mt-14 grid grid-cols-12 gap-4">
                    <p className="fade col-span-12 sm:col-span-6 lg:col-span-4 lg:col-start-9 text-[#7a7873] text-base sm:text-lg leading-relaxed max-w-[26rem]"
                       style={d(1100)}>
                        Apps, websites, and AI — built by a team who cares about craft.
                    </p>
                </div>
            </Reveal>

            <Reveal className="shell relative">
                <div className="rule" style={d(900)} />
                <div className="flex justify-between items-center py-5 mono text-[#7a7873]">
                    <span className="fade" style={d(1300)}>
                        26.2041° S, 28.0473° E
                    </span>
                    <span className="fade flex items-center justify-end gap-4" style={d(1500)}>
                        <span>Scroll</span>
                        <span className="scroll-cue" aria-hidden="true" />
                    </span>
                </div>
            </Reveal>
        </section>
    );
}

function Practice() {
    return (
        <section id="practice" className="shell py-28 sm:py-40">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-8">
                <div className="lg:col-span-3">
                    <SectionLabel index={1} title="Practice" />
                </div>

                <div className="lg:col-span-9">
                    <Reveal>
                        <p className="display text-[clamp(1.9rem,4.2vw,4rem)] !leading-[1.04] max-w-[18ch]">
                            <span className="mask"><span style={d(0)}>Small team.</span></span>
                            <span className="mask text-[#4a4945]"><span style={d(120)}>Considered software.</span></span>
                        </p>
                    </Reveal>

                    <div className="mt-20 sm:mt-28 rows">
                        {capabilities.map((cap, i) => (
                            <Reveal key={cap.label} className="row-item">
                                <div className="rule" style={d(i * 90)} />
                                <div className="grid grid-cols-12 gap-4 py-7 sm:py-9 items-baseline">
                                    <span className="fade mono text-[#7a7873] col-span-2 sm:col-span-1" style={d(150 + i * 90)}>
                                        {pad(i + 1)}
                                    </span>
                                    <h3 className="fade col-span-10 sm:col-span-5 text-2xl sm:text-3xl tracking-tight" style={d(200 + i * 90)}>
                                        <span className="row-name">{cap.label}</span>
                                    </h3>
                                    <p className="fade col-span-10 col-start-3 sm:col-span-6 sm:col-start-auto text-[#7a7873] leading-relaxed" style={d(260 + i * 90)}>
                                        {cap.desc}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                        <Reveal><div className="rule" /></Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Work() {
    return (
        <section id="work" className="shell py-28 sm:py-40">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-8">
                <div className="lg:col-span-3">
                    <SectionLabel index={2} title="Work" />
                </div>

                <div className="lg:col-span-9 rows">
                    {products.map((p, i) => (
                        <Reveal key={p.name} className="row-item">
                            <div className="rule" style={d(i * 90)} />
                            <a
                                href={p.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group grid grid-cols-12 gap-4 py-10 sm:py-14 items-end no-underline text-[#e9e7e2]"
                            >
                                <span className="fade mono text-[#7a7873] col-span-12 sm:col-span-1 self-start sm:pt-4" style={d(100 + i * 90)}>
                                    {pad(i + 1)}
                                </span>
                                <span className="col-span-12 sm:col-span-6 display text-[clamp(3rem,7vw,7rem)]">
                                    <span className="mask"><span style={d(150 + i * 90)}><span className="row-name">{p.name}</span></span></span>
                                </span>
                                <span className="fade col-span-12 sm:col-span-5 flex flex-col gap-3 sm:pb-3" style={d(300 + i * 90)}>
                                    <span className="mono text-[#7a7873]">{p.tag}</span>
                                    <span className="text-[#bdbab3] leading-relaxed">{p.desc}</span>
                                    <span className="mono text-[#e9e7e2] flex items-center gap-2">
                                        {p.cta}
                                        <span className="row-arrow" aria-hidden="true">↗</span>
                                    </span>
                                </span>
                            </a>
                        </Reveal>
                    ))}
                    <Reveal><div className="rule" /></Reveal>
                </div>
            </div>
        </section>
    );
}

function Contact() {
    return (
        <section id="contact" className="shell pt-28 sm:pt-40 pb-24 sm:pb-32">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-8">
                <div className="lg:col-span-3">
                    <SectionLabel index={3} title="Contact" />
                </div>

                <Reveal className="lg:col-span-9">
                    <p className="display text-[clamp(1.9rem,4.2vw,4rem)] !leading-[1.04]">
                        <span className="mask"><span style={d(0)}>Have something</span></span>
                        <span className="mask text-[#4a4945]"><span style={d(120)}>worth building?</span></span>
                    </p>

                    <a
                        href="mailto:hello@unit326.com"
                        className="fade mt-14 sm:mt-20 inline-flex items-baseline gap-4 no-underline text-[#e9e7e2]"
                        style={d(400)}
                    >
                        <span className="underline-draw display text-[clamp(1.6rem,5.4vw,5.5rem)] break-all sm:break-normal">
                            hello@unit326.com
                        </span>
                    </a>
                </Reveal>
            </div>
        </section>
    );
}

function Home() {
    return (
        <div className="site">
            <div className="grain" aria-hidden="true" />
            <Header />
            <main>
                <Hero />
                <Practice />
                <Work />
                <Contact />
            </main>
            <Footer />
        </div>
    );
}

export default Home;
